import React, { forwardRef } from 'react';
import classNames from 'classnames';
import {
  Button as BpButton,
  ButtonProps as BpButtonProps,
  Intent,
  IconName,
  MaybeElement,
} from '@blueprintjs/core';
import styles from './Button.module.scss';

export type ButtonVariant = 'solid' | 'outlined' | 'minimal' | 'link';
export type ButtonSize = 'small' | 'medium' | 'large';
export type ButtonIntent = 'none' | 'primary' | 'success' | 'warning' | 'danger';

export interface ButtonProps
  extends Omit<
    React.HTMLAttributes<HTMLElement>,
    'onClick'
  > {
  /** Visual treatment. `solid` renders a filled surface, `outlined` a bordered
   *  one. Defaults to `solid` when an `intent` is provided, `outlined` otherwise. */
  variant?: ButtonVariant;
  /** Semantic intent — drives color. Maps to Blueprint intents. */
  intent?: ButtonIntent;
  /** Vertical density. */
  size?: ButtonSize;
  /** Show a spinner and disable the button. */
  loading?: boolean;
  /** Icon rendered before the label. */
  icon?: IconName | MaybeElement;
  /** Icon rendered after the label. */
  rightIcon?: IconName | MaybeElement;
  /** Stretch to fill the container width. */
  fullWidth?: boolean;
  /** Disable interaction. */
  disabled?: boolean;
  /** Native button type (default: `'button'` — never accidentally submit a form). */
  type?: 'button' | 'submit' | 'reset';
  onClick?: React.MouseEventHandler<HTMLElement>;
}

const intentMap: Record<ButtonIntent, Intent> = {
  none: Intent.NONE,
  primary: Intent.PRIMARY,
  success: Intent.SUCCESS,
  warning: Intent.WARNING,
  danger: Intent.DANGER,
};

/**
 * Unified application button. Wraps Blueprint's `<Button>` with a small,
 * explicit API (`variant` / `intent` / `size`) and consistent geometry from
 * the design tokens in `_variables.scss`.
 *
 * Prefer this over importing `Button` directly from `@blueprintjs/core` or
 * styling raw `<button>` elements.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant,
    intent = 'none',
    size = 'medium',
    loading = false,
    icon,
    rightIcon,
    fullWidth = false,
    disabled = false,
    className,
    type = 'button',
    children,
    ...rest
  },
  ref,
) {
  const resolvedVariant: ButtonVariant =
    variant ?? (intent === 'none' ? 'outlined' : 'solid');

  const bpProps: BpButtonProps = {
    intent: intentMap[intent],
    loading,
    icon,
    rightIcon,
    disabled,
    small: size === 'small',
    large: size === 'large',
    minimal: resolvedVariant === 'minimal',
    fill: fullWidth,
  };

  return (
    <BpButton
      {...bpProps}
      {...rest}
      type={type}
      elementRef={ref as React.RefObject<HTMLButtonElement>}
      className={classNames(
        styles.button,
        styles[resolvedVariant],
        fullWidth && styles.fullWidth,
        className,
      )}
    >
      {children}
    </BpButton>
  );
});
